import {
  format as formatSDK,
  formatDate as formatDatetimeSDK,
  FormatDateOptions,
} from '@dynatrace-sdk/units';

/**
 * Default datetime format options (date + time),
 * e.g. "12/21/2024, 5:45:30 PM" for US English locale.
 * @internal
 */
export const DATETIME_DEFAULT_FORMAT_OPTIONS: FormatDateOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
};

/**
 * Formats the date with the default configuration
 * @internal
 */
export function formatDatetimeDefault(value: number | Date) {
  const valueAsDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(valueAsDate.getTime())) {
    return '';
  }

  return formatDatetimeSDK(valueAsDate, DATETIME_DEFAULT_FORMAT_OPTIONS);
}

/**
 * Converts a date-like value to an ISO 8601 string, or `undefined` if the
 * value cannot be parsed as a valid date.
 *
 * Numeric strings are treated as epoch milliseconds (not as a year) so that
 * `"0"` resolves to `1970-01-01T00:00:00.000Z` rather than `2000-01-01`.
 *
 * @internal
 */
export function formatDateToISOString(dateValue: unknown): string | undefined {
  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime())
      ? undefined
      : dateValue.toISOString();
  }
  if (typeof dateValue !== 'string') {
    return undefined;
  }
  const asNumber = Number(dateValue);
  const date = Number.isFinite(asNumber)
    ? new Date(asNumber)
    : new Date(dateValue);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/**
 * Formats a given number with default currency configuration
 * (no currency symbol, but two fraction digits, e.g. 12345.6789 → 12,345.68).
 *
 * @param value - The number to be formatted as currency.
 * @param formatOptions - The format options configured by the consumer.
 *
 * @internal
 */
export function formatCurrencyDefault(
  value: number,
  formatOptions: Omit<Intl.NumberFormatOptions, 'currency' | 'style'> & {
    locale?: string | string[];
    abbreviate?: boolean;
  } = {},
) {
  return formatSDK(value, {
    abbreviate: false,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...formatOptions,
  });
}
